import { TennisPlayerScore, TennisScore } from '../types/types';

/** Games each player must hold for the set's next game to be a tiebreak. */
export const TIEBREAK_AT_GAMES = 6;

/** Games that win a set, by a margin of two; seven wins it 7-5 or in the tiebreak. */
const GAMES_TO_WIN_SET = 6;
const WINNING_MARGIN = 2;
/** Points that win a tiebreak, by a margin of two. */
const TIEBREAK_POINTS = 7;

const POINT_CALLS = ['0', '15', '30', '40'];
const FORTY = 3;

/** Sets that win the match, per rule-set code. */
const SETS_TO_WIN: Record<string, number> = {
  TEN_BO1_TB: 1,
  TEN_BO3_TB: 2,
};

export interface PairOfLabels {
  first: string;
  second: string;
}

export interface PairOfCounts {
  first: number;
  second: number;
}

/** A player's standing in one state, read by DB competitor id. */
export interface PlayerPair {
  first: TennisPlayerScore;
  second: TennisPlayerScore;
}

export type PointTag = 'BREAK_POINT' | 'SET_POINT' | 'MATCH_POINT';

/** What the point about to be played can decide. */
export interface PointSituation {
  /** Break points the receiver holds: 3 at 0-40, 1 at 30-40 or advantage receiver. */
  breakPoints: number;
  setPointFor?: number;
  matchPointFor?: number;
  deuce: boolean;
}

export interface TennisPoint {
  sequence: number;
  timestamp: number;
  serverCompetitorId?: number;
  winnerCompetitorId: number;
  tiebreak: boolean;
  /** The receiver could break serve on it. */
  breakPoint: boolean;
  /** The game score after the point; absent on the point that won the game. */
  score?: PairOfLabels;
  /** The most decisive thing the point could settle, and for whom. */
  tag?: PointTag;
  tagFor?: number;
}

export interface TennisGame {
  set: number;
  game: number;
  serverCompetitorId?: number;
  winnerCompetitorId: number;
  /** Raw points won in the game, first competitor then second. */
  points: PairOfCounts;
  tiebreak: boolean;
  /** The receiver won a regular game. */
  breakOfServe: boolean;
  /** Games in the set after this one. */
  gamesAfter: PairOfCounts;
  /** The score before the winning point; the final points of a tiebreak. */
  wonFrom: PairOfLabels;
  pointLog: TennisPoint[];
}

export interface GameLog {
  games: TennisGame[];
  /** The points of the game in play. */
  current: TennisPoint[];
  /** False when the stored points do not follow one another, so the log stops before the first break. */
  complete: boolean;
}

/** The most recent state: the backend sends the history oldest first. */
export function latestScore(scores?: TennisScore[]): TennisScore | undefined {
  return scores && scores.length > 0 ? scores[scores.length - 1] : undefined;
}

export function playerPair(score: TennisScore | undefined, firstId: number, secondId: number): PlayerPair | undefined {
  const first = score?.players.find(p => p.competitorId === firstId);
  const second = score?.players.find(p => p.competitorId === secondId);
  return first && second ? { first, second } : undefined;
}

export function isTiebreak(firstGames: number, secondGames: number): boolean {
  return firstGames === TIEBREAK_AT_GAMES && secondGames === TIEBREAK_AT_GAMES;
}

/** Sets that win the match; undefined for a rule set this page does not know. */
export function setsToWin(ruleSet?: string): number | undefined {
  return ruleSet ? SETS_TO_WIN[ruleSet] : undefined;
}

/**
 * The game score as a tennis call: 0 / 15 / 30 / 40, 40-40 from deuce on and `Ad` for the player ahead;
 * a tiebreak is shown in plain counts.
 */
export function pointCalls(first: number, second: number, tiebreak: boolean): PairOfLabels {
  if (tiebreak) {
    return { first: String(first), second: String(second) };
  }
  if (first >= FORTY && second >= FORTY) {
    return {
      first: first > second ? 'Ad' : '40',
      second: second > first ? 'Ad' : '40',
    };
  }
  return { first: POINT_CALLS[Math.min(first, FORTY)], second: POINT_CALLS[Math.min(second, FORTY)] };
}

export function isDeuce(first: number, second: number, tiebreak: boolean): boolean {
  return !tiebreak && first >= FORTY && first === second;
}

/** One more point wins the game for the player on `own` points against `other`. */
function isGamePoint(own: number, other: number, tiebreak: boolean): boolean {
  return own >= (tiebreak ? TIEBREAK_POINTS - 1 : FORTY) && own > other;
}

/**
 * What the next point can decide, read from the state before it and its server. A match point needs
 * the rule set's sets to win; without it a set point is reported.
 */
export function pointSituation(
  pair: PlayerPair,
  serverId: number | undefined,
  matchSetsToWin?: number,
): PointSituation {
  const tiebreak = isTiebreak(pair.first.gamesWonCurrentSet, pair.second.gamesWonCurrentSet);
  const players = [pair.first, pair.second];
  const situation: PointSituation = {
    breakPoints: 0,
    deuce: isDeuce(pair.first.currentGamePoints, pair.second.currentGamePoints, tiebreak),
  };
  const server = players.find(p => p.competitorId === serverId);
  const receiver = players.find(p => p.competitorId !== serverId);
  if (!tiebreak && server && receiver
    && isGamePoint(receiver.currentGamePoints, server.currentGamePoints, false)) {
    situation.breakPoints = receiver.currentGamePoints - server.currentGamePoints;
  }
  players.forEach((own, index) => {
    const other = players[1 - index];
    if (!isGamePoint(own.currentGamePoints, other.currentGamePoints, tiebreak)) {
      return;
    }
    const gamesAfter = own.gamesWonCurrentSet + 1;
    const winsSet = tiebreak
      || (gamesAfter >= GAMES_TO_WIN_SET && gamesAfter - other.gamesWonCurrentSet >= WINNING_MARGIN);
    if (!winsSet) {
      return;
    }
    if (matchSetsToWin !== undefined && own.setsWon + 1 >= matchSetsToWin) {
      situation.matchPointFor = own.competitorId;
    } else {
      situation.setPointFor = own.competitorId;
    }
  });
  return situation;
}

/** The most decisive thing a point can settle: the match, then the set, then a break of serve. */
function tagOf(situation: PointSituation, receiverId: number): Pick<TennisPoint, 'tag' | 'tagFor'> {
  if (situation.matchPointFor !== undefined) {
    return { tag: 'MATCH_POINT', tagFor: situation.matchPointFor };
  }
  if (situation.setPointFor !== undefined) {
    return { tag: 'SET_POINT', tagFor: situation.setPointFor };
  }
  return situation.breakPoints > 0 ? { tag: 'BREAK_POINT', tagFor: receiverId } : {};
}

const ZERO = (competitorId: number): TennisPlayerScore =>
  ({ competitorId, setsWon: 0, gamesWonCurrentSet: 0, currentGamePoints: 0 });

/** One point to `winnerId` and nothing else changed. */
function isPointWon(before: PlayerPair, after: PlayerPair, winnerFirst: boolean): boolean {
  const [won, lost, wonAfter, lostAfter] = winnerFirst
    ? [before.first, before.second, after.first, after.second]
    : [before.second, before.first, after.second, after.first];
  return wonAfter.currentGamePoints === won.currentGamePoints + 1
    && lostAfter.currentGamePoints === lost.currentGamePoints
    && wonAfter.gamesWonCurrentSet === won.gamesWonCurrentSet
    && lostAfter.gamesWonCurrentSet === lost.gamesWonCurrentSet
    && wonAfter.setsWon === won.setsWon
    && lostAfter.setsWon === lost.setsWon;
}

/** One game to the winner, or the set when that game closed it; the new game starts at 0-0. */
function isGameWon(before: PlayerPair, after: PlayerPair, winnerFirst: boolean): boolean {
  const [won, lost, wonAfter, lostAfter] = winnerFirst
    ? [before.first, before.second, after.first, after.second]
    : [before.second, before.first, after.second, after.first];
  if (wonAfter.currentGamePoints !== 0 || lostAfter.currentGamePoints !== 0) {
    return false;
  }
  const gameInSet = wonAfter.setsWon === won.setsWon && lostAfter.setsWon === lost.setsWon
    && wonAfter.gamesWonCurrentSet === won.gamesWonCurrentSet + 1
    && lostAfter.gamesWonCurrentSet === lost.gamesWonCurrentSet;
  const setClosed = wonAfter.setsWon === won.setsWon + 1 && lostAfter.setsWon === lost.setsWon
    && wonAfter.gamesWonCurrentSet === 0 && lostAfter.gamesWonCurrentSet === 0;
  return gameInSet || setClosed;
}

/**
 * The finished games and the points of the game in play, rebuilt from the point states. Each point must
 * follow the previous one by sequence and move the score by exactly that point; the log stops at the
 * first state that does not.
 */
export function gameLog(
  scores: TennisScore[] | undefined,
  firstId: number,
  secondId: number,
  matchSetsToWin?: number,
): GameLog {
  const points = (scores ?? []).filter(s => s.sequence > 0);
  const games: TennisGame[] = [];
  let current: TennisPoint[] = [];
  let before: PlayerPair = { first: ZERO(firstId), second: ZERO(secondId) };
  let lastSequence = 0;
  let gameServer: number | undefined;

  for (const point of points) {
    if (point.sequence === lastSequence) {
      continue;
    }
    const after = playerPair(point, firstId, secondId);
    const winner = point.pointWinnerCompetitorId;
    if (point.sequence !== lastSequence + 1 || !after || (winner !== firstId && winner !== secondId)) {
      return { games, current, complete: false };
    }
    const winnerFirst = winner === firstId;
    gameServer = gameServer ?? point.serverCompetitorId;
    const tiebreak = isTiebreak(before.first.gamesWonCurrentSet, before.second.gamesWonCurrentSet);
    const situation = pointSituation(before, point.serverCompetitorId, matchSetsToWin);
    const raw = {
      first: before.first.currentGamePoints + (winnerFirst ? 1 : 0),
      second: before.second.currentGamePoints + (winnerFirst ? 0 : 1),
    };
    const logged: TennisPoint = {
      sequence: point.sequence,
      timestamp: point.timestamp,
      serverCompetitorId: point.serverCompetitorId,
      winnerCompetitorId: winner,
      tiebreak,
      breakPoint: situation.breakPoints > 0,
      ...tagOf(situation, point.serverCompetitorId === firstId ? secondId : firstId),
    };

    if (isGameWon(before, after, winnerFirst)) {
      current.push(logged);
      games.push({
        set: before.first.setsWon + before.second.setsWon + 1,
        game: before.first.gamesWonCurrentSet + before.second.gamesWonCurrentSet + 1,
        serverCompetitorId: gameServer,
        winnerCompetitorId: winner,
        points: raw,
        tiebreak,
        breakOfServe: !tiebreak && gameServer !== undefined && gameServer !== winner,
        gamesAfter: {
          first: before.first.gamesWonCurrentSet + (winnerFirst ? 1 : 0),
          second: before.second.gamesWonCurrentSet + (winnerFirst ? 0 : 1),
        },
        wonFrom: tiebreak
          ? pointCalls(raw.first, raw.second, true)
          : pointCalls(before.first.currentGamePoints, before.second.currentGamePoints, false),
        pointLog: current,
      });
      current = [];
      gameServer = undefined;
    } else if (isPointWon(before, after, winnerFirst)) {
      current.push({ ...logged, score: pointCalls(raw.first, raw.second, tiebreak) });
    } else {
      return { games, current, complete: false };
    }
    before = after;
    lastSequence = point.sequence;
  }
  return { games, current, complete: true };
}

/**
 * Who serves the next point. A stored state names the server of the point it describes, so the next
 * server is worked out from the rotation: the other player after a game, and in a tiebreak after the
 * first point and every two after it. Undefined before the first point and once the match is over.
 */
export function nextServer(scores: TennisScore[] | undefined, firstId: number, secondId: number): number | undefined {
  const latest = latestScore(scores);
  const points = (scores ?? []).filter(s => s.sequence > 0);
  const last = points[points.length - 1];
  const pair = playerPair(last, firstId, secondId);
  if (!latest || latest.gamePhase !== 'SET_IN_PROGRESS' || !last || !pair) {
    return undefined;
  }
  const server = last.serverCompetitorId;
  if (server !== firstId && server !== secondId) {
    return undefined;
  }
  const other = server === firstId ? secondId : firstId;
  const gameEnded = pair.first.currentGamePoints === 0 && pair.second.currentGamePoints === 0;
  if (!gameEnded) {
    const tiebreak = isTiebreak(pair.first.gamesWonCurrentSet, pair.second.gamesWonCurrentSet);
    const played = pair.first.currentGamePoints + pair.second.currentGamePoints;
    return tiebreak && played % 2 === 1 ? other : server;
  }
  const previous = points[points.length - 2];
  const previousPair = playerPair(previous, firstId, secondId);
  if (!previous || previous.sequence !== last.sequence - 1 || !previousPair) {
    return undefined;
  }
  if (!isTiebreak(previousPair.first.gamesWonCurrentSet, previousPair.second.gamesWonCurrentSet)) {
    return other;
  }
  // The player who served first in the tiebreak receives first in the next set.
  const tiebreakPoints = previousPair.first.currentGamePoints + previousPair.second.currentGamePoints + 1;
  const lastServedFirst = (tiebreakPoints >> 1) % 2 === 0;
  return lastServedFirst ? other : server;
}

/** Breaks of serve each player made in one set. */
export function breaksInSet(log: GameLog, set: number, firstId: number): PairOfCounts {
  const breaks = { first: 0, second: 0 };
  log.games.filter(g => g.set === set && g.breakOfServe).forEach(g => {
    if (g.winnerCompetitorId === firstId) breaks.first++; else breaks.second++;
  });
  return breaks;
}
